import React, { useMemo, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import queryString from "query-string";
import moment from "moment";

import "./Calendar.scss";

import HeaderWrapper from "../../common/HeaderWrapper";
import Loading from "../../common/Loading";

import { formatDate } from "../../../helpers/date";

import CalendarSlot from "./CalendarSlot";

const Calendar = ({
  getPTORequests,
  data,
  isNightMode,
  department,
  dragState,
  codes
}) => {
  const [start, setStart] = useState(
    moment().startOf("week").subtract(1, "day")
  );

  const [end, setEnd] = useState(moment().startOf("week").add(2, "week"));

  const [loading, setLoading] = useState(false);
  const [filteredData, setFilteredData] = useState(null);

  const history = useHistory();

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      const query = queryString.stringify(
        {
          start_date: start
            .clone()
            .subtract(4, "week")
            .endOf("day")
            .toISOString(),
          end_date: end.clone().add(4, "week").endOf("day").toISOString(),
          department: department ? [department] : ""
        },
        {
          arrayFormat: "bracket"
        }
      );
      await getPTORequests(query);
    };
    getData();
  }, [getPTORequests, start, end, department]);

  useEffect(() => {
    if (data) {
      setLoading(true);
      const newData = data?.filter(
        (ptoItem) =>
          ptoItem.status.label !== "Declined" &&
          ptoItem.status.label !== "Cancelled" &&
          ptoItem.status.label !== "Pending Approval" &&
          ptoItem.employee?.is_active
      );

      setFilteredData(newData);
      setLoading(false);
    }
  }, [data]);

  const periodCodes = useMemo(
    () =>
      codes?.filter(
        (code) => code.entity === "PTO Request" && code.field === "period"
      ),
    [codes]
  );

  const weeks = useMemo(() => {
    if (!loading && filteredData) {
      const dates = [];

      const currDate = moment(start).startOf("day");
      const lastDate = moment(end).startOf("day");

      let index = 0;

      while (currDate.add(1, "days").diff(lastDate) < 0) {
        const date = currDate.clone().toDate();
        dates.push({
          date,
          day: date.toLocaleString("en-us", {
            day: "numeric"
          }),
          weekday: date
            .toLocaleString("en-us", {
              weekday: "long"
            })
            .substring(0, 3),
          showWeekday: index < 7,
          isWeekend: date.getDay() === 0 || date.getDay() === 6,
          data: filteredData
            .map((ptoRequest) => ({
              pto: ptoRequest.days?.find(
                (ptoDate) =>
                  formatDate(ptoDate.date, "PPPP") ===
                  formatDate(currDate.toDate(), "PPPP")
              ),
              employee: ptoRequest.employee?.full_name,
              id: ptoRequest._id
            }))
            .filter((a) => a.pto)
        });
        index += 1;
      }

      return dates;
    }
  }, [start, end, loading, filteredData]);

  const onPreviousWeeksClick = () => {
    setStart(start.clone().subtract(2, "weeks"));
    setEnd(end.clone().subtract(2, "weeks"));
  };

  const onNextWeeksClick = () => {
    setStart(start.clone().add(2, "weeks"));
    setEnd(end.clone().add(2, "weeks"));
  };

  const onRequestPTOClick = () => {
    history.push(`/app/pto/add`);
  };

  const onTodayClick = () => {
    setStart(moment().startOf("week").subtract(1, "day"));
    setEnd(moment().startOf("week").add(2, "week"));
  };

  const handleClick = (origin) => {
    if (!dragState.dragInProcess && dragState.dragOver) {
      switch (origin) {
        case "prev":
          onPreviousWeeksClick();
          break;
        case "next":
          onNextWeeksClick();
          break;
        case "today":
          onTodayClick();
          break;
        case "pto":
          onRequestPTOClick();
          break;
        default:
          break;
      }
    }
  };

  return (
    <React.Fragment>
      <HeaderWrapper
        breadcrumbs={[{ label: "PTO Calendar" }]}
        onClickAction={[
          {
            action: () => handleClick("today"),
            text: "Today",
            className: "btn-info"
          },
          {
            action: () => handleClick("pto"),
            text: "Request PTO",
            className: "btn-success"
          }
        ]}
      />
      <div className="dashboard-calendar-container content-wrapper">
        {loading && (
          <div className="loading-loader-load-container">
            <Loading active={loading} />
          </div>
        )}
        {!loading && filteredData && (
          <React.Fragment>
            <h3>{`${start.clone().add(1, "day").format("LL")} - ${end
              .clone()
              .subtract(1, "day")
              .format("LL")}`}</h3>
            <div className="slots-container surface-secondary">
              <IoChevronBack
                className="arrow left"
                role="button"
                onClick={() => handleClick("prev")}
              />
              <div className="slots-wrapper">
                {weeks.map((week) => (
                  <CalendarSlot
                    periodCodes={periodCodes}
                    week={week}
                    key={week.day}
                    isNightMode={isNightMode}
                    isWeekend={week.isWeekend}
                  />
                ))}
              </div>
              <IoChevronForward
                className="arrow right"
                role="button"
                onClick={() => handleClick("next")}
              />
            </div>
          </React.Fragment>
        )}
      </div>
    </React.Fragment>
  );
};

Calendar.propTypes = {
  getPTORequests: PropTypes.func,
  data: PropTypes.array,
  isNightMode: PropTypes.bool,
  department: PropTypes.string,
  dragState: PropTypes.object,
  codes: PropTypes.array
};

Calendar.defaultProps = {
  getPTORequests: () => null,
  data: [],
  isNightMode: false,
  department: "",
  dragState: {},
  codes: []
};

export default Calendar;
